import {useMemo} from "react";
import SentryEchart from "../../../../../../../../components/echarts/sentry-echart.jsx";
import useGraphViewportWidth from "../../../../../../../../hooks/use-graph-viewport-width.js";
import {fluidPx} from "../../../../../../../../utils/responsive-graph.js";
import ProjectUtils from "../../../../../../../../utils/project-utils.js";

export default function RelationshipProtected({ data }) {
    const vw = useGraphViewportWidth();
    const aggressors = data?.aggressors;

    const ageRanges = useMemo(() => Array.isArray(aggressors?.ageRanges)
        ? aggressors.ageRanges
            .filter(item => item.label?.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR") !== "nao informado")
            .map(item => ({
                label: item.label ?? "",
                value: Number(item.qty) || 0,
            }))
        : [], [aggressors?.ageRanges]);

    const familyConnectionsOption = useMemo(() => {
        const items = Array.isArray(aggressors?.familyConnections)
            ? aggressors.familyConnections.map(item => ({
                label: item.label ?? "",
                value: Number(item.qty) || 0,
            }))
            : [];
        const labelFs = fluidPx(12, 21, vw);
        const valueFs = fluidPx(14, 20, vw);
        const max = Math.max(...items.map(item => item.value), 1);
        const labels = items.map(item => ProjectUtils.sentenceCase(item.label));
        const values = items.map(item => item.value);

        return {
            grid: {
                top: labelFs,
                left: "6%",
                right: "10%",
                bottom: 0,
                outerBounds: {
                    top: labelFs,
                    left: "6%",
                    right: "10%",
                    bottom: 0,
                },
                outerBoundsContain: "axisLabel",
            },
            tooltip: {
                show: true,
                appendToBody: true,
                confine: false,
                extraCssText: "max-width: 320px; white-space: normal;",
                formatter: params => `${params.name}: ${params.value}`,
            },
            xAxis: {
                type: "value",
                max,
                show: false,
            },
            yAxis: {
                type: "category",
                data: labels,
                inverse: true,
                axisLabel: {show: false},
                axisLine: {show: false},
                axisTick: {show: false},
            },
            series: [
                {
                    type: "bar",
                    barWidth: 13,
                    barCategoryGap: "24%",
                    itemStyle: {
                        color: {
                            type: "linear",
                            x: 0,
                            y: 0,
                            x2: 1,
                            y2: 0,
                            colorStops: [
                                {offset: 0, color: "#003878"},
                                {offset: 0.28, color: "#0080ED"},
                                {offset: 0.52, color: "#1CEAE6"},
                                {offset: 0.72, color: "#3E98D9"},
                                {offset: 1, color: "#003878"},
                            ],
                        },
                        borderRadius: 20,
                    },
                    label: {
                        show: true,
                        position: [0, -4],
                        align: "left",
                        verticalAlign: "bottom",
                        color: "#AAAAAA",
                        fontSize: labelFs,
                        fontWeight: 400,
                        formatter: params => labels[params.dataIndex],
                    },
                    showBackground: true,
                    backgroundStyle: {
                        color: "#3C3C4A",
                        borderRadius: 12,
                    },
                    data: values,
                },
                {
                    type: "bar",
                    barWidth: 13,
                    barCategoryGap: "24%",
                    barGap: "-100%",
                    silent: true,
                    tooltip: {show: false},
                    itemStyle: {color: "transparent"},
                    label: {
                        show: true,
                        position: "right",
                        distance: 5,
                        color: "#FFFFFF",
                        fontSize: valueFs,
                        fontWeight: 600,
                        formatter: params => values[params.dataIndex],
                    },
                    data: values.map(() => max),
                },
            ],
        };
    }, [aggressors?.familyConnections, vw]);

    const ageRangesOption = useMemo(() => {
        const values = ageRanges.map(item => item.value);
        const maxValue = Math.max(...values, 1);

        return {
            grid: {
                top: 24,
                left: 8,
                right: 8,
                bottom: 0,
                containLabel: true,
            },
            tooltip: {
                trigger: "axis",
                axisPointer: {type: "shadow"},
                formatter: params => {
                    const coloredBar = params.find(item => item.seriesName === "Quantidade");
                    const item = ageRanges[coloredBar?.dataIndex];

                    return item?.label ? `${item.label}: <b>${item.value}</b>` : "";
                },
            },
            xAxis: {
                type: "category",
                boundaryGap: true,
                data: ageRanges.map(item => item.label),
                axisLabel: {
                    color: "#AAAAAA",
                    fontSize: 12,
                    interval: 0,
                    width: 55,
                    overflow: "truncate",
                    ellipsis: "...",
                },
                axisLine: {
                    show: true,
                    lineStyle: {color: "#555762"},
                },
                axisTick: {show: false},
            },
            yAxis: {
                type: "value",
                min: 0,
                max: maxValue,
                minInterval: 1,
                axisLabel: {show: false},
                axisTick: {show: false},
                splitLine: {show: false},
                axisLine: {show: false},
            },
            series: [
                {
                    name: "Quantidade",
                    type: "bar",
                    barWidth: "68%",
                    itemStyle: {
                        borderRadius: [4, 4, 0, 0],
                        color: {
                            type: "linear",
                            x: 0,
                            y: 1,
                            x2: 1,
                            y2: 0,
                            colorStops: [
                                {offset: 0, color: "#003878"},
                                {offset: 0.5, color: "#0080ED"},
                                {offset: 1, color: "#1CEAE6"},
                            ],
                        },
                    },
                    label: {
                        show: true,
                        position: "top",
                        distance: 8,
                        color: "#FDFCFF",
                        fontSize: 12,
                        fontWeight: "bold",
                        formatter: params => values[params.dataIndex],
                    },
                    data: values,
                    z: 2,
                },
            ],
        };
    }, [ageRanges]);

    return (
        <article className="col-span-4 box-border -mt-2 grid h-[calc(100%+12px)] min-h-0 min-w-0 grid-rows-[minmax(0,1fr)_clamp(145px,14vh,175px)] gap-y-2">
            <section className="min-h-0 min-w-0 overflow-hidden flex">
                <SentryEchart
                    className="h-full min-h-0 w-full"
                    option={familyConnectionsOption}
                    title="Vínculo com o protegido"
                    titleClass="mb-0! translate-y-1"
                />
            </section>
            <section className="-mt-2 flex min-h-0 flex-col">
                <h3 className="shrink-0 px-4 text-center text-(length:--font-md)! text-[#AAAAAA]">
                    Idade
                </h3>

                <div className="min-h-0 flex-1 px-2 pb-1">
                    <SentryEchart
                        className="h-full min-h-0 w-full"
                        option={ageRangesOption}
                    />
                </div>
            </section>
        </article>
    );
}
